import React, { useEffect, useRef } from 'react'
import { useSearch } from '../../hooks/useSearch'
import LoadingSpinner from '../ui/LoadingSpinner/LoadingSpinner'
import ProductItemSearch from '../ui/ProductItemSearch/ProductItemSearch'
import styles from './SearchPopup.module.css'

interface SearchPopupProps {
  isOpen: boolean
  onClose: () => void
}

const SearchPopup: React.FC<SearchPopupProps> = ({ isOpen, onClose }) => {
  const { searchQuery, setSearchQuery, searchResults, isSearching, searchError } = useSearch()
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus()
    } else if (!isOpen) {
      // Очищаємо пошук при закритті
      setSearchQuery('')
    }
  }, [isOpen, setSearchQuery])

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleEscape)
      document.body.style.overflow = 'hidden'
    }

    return () => {
      document.removeEventListener('keydown', handleEscape)
      document.body.style.overflow = ''
    }
  }, [isOpen, onClose])

  const handleSuggestionClick = (suggestion: string) => {
    setSearchQuery(suggestion)
    if (inputRef.current) {
      inputRef.current.focus()
    }
  }

  const handleProductClick = () => {
    onClose()
  }

  const handleClearSearch = () => {
    setSearchQuery('')
    if (inputRef.current) {
      inputRef.current.focus()
    }
  }

  if (!isOpen) return null

  return (
    <>
      <div className={styles.overlay} onClick={onClose} />
      <div className={styles.searchPopup}>
        <div className={styles.searchHeader}>
          <div className={styles.searchInput}>
            <input
              type="text"
              placeholder="Що для тебе знайти?"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button type="button" className={styles.searchButton} onClick={handleClearSearch}>
                очистити
              </button>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className={styles.closeButton}
            aria-label="Закрити пошук"
          >
            <svg
              width="32"
              height="32"
              viewBox="0 0 32 32"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M8 26.625L26.625 8M8 8L26.625 26.625"
                stroke="#BFBFBF"
                strokeWidth="1.25"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>

        {!searchQuery && (
          <div className={styles.searchSuggestions}>
            <div className={styles.suggestionsTitle}>Часто шукають</div>
            <div className={styles.suggestionsList}>
              <button
                className={styles.suggestionItem}
                onClick={() => handleSuggestionClick('кава')}
              >
                кава
              </button>
              <button
                className={styles.suggestionItem}
                onClick={() => handleSuggestionClick('дріпи')}
              >
                дріпи
              </button>
              <button
                className={styles.suggestionItem}
                onClick={() => handleSuggestionClick('кава колумбія')}
              >
                кава колумбія
              </button>
              <button
                className={styles.suggestionItem}
                onClick={() => handleSuggestionClick('ефіопія')}
              >
                ефіопія
              </button>
              <button
                className={styles.suggestionItem}
                onClick={() => handleSuggestionClick('пачка дрипів')}
              >
                пачка дрипів
              </button>
            </div>
          </div>
        )}

        {searchQuery && (
          <div className={styles.resultsContainer}>
            <div className={styles.resultsTitle}>
              {isSearching ? (
                <div className={styles.searchingIndicator}>
                  <LoadingSpinner size="small" />
                  <span>Пошук...</span>
                </div>
              ) : searchError ? (
                <div className={styles.errorMessage}>{searchError}</div>
              ) : (
                `Знайдено товарів - ${searchResults.length}`
              )}
            </div>

            {!isSearching && !searchError && (
              <div className={styles.resultsList}>
                {searchResults.length > 0 ? (
                  searchResults.map(product => (
                    <div
                      key={product.id}
                      className={styles.resultItem}
                      onClick={handleProductClick}
                    >
                      <ProductItemSearch product={product} />
                    </div>
                  ))
                ) : searchQuery.trim() ? (
                  <div className={styles.noResults}>
                    <p>За вашим запитом "{searchQuery}" нічого не знайдено</p>
                    <p>Спробуйте змінити пошуковий запит</p>
                  </div>
                ) : null}
              </div>
            )}
          </div>
        )}
      </div>
    </>
  )
}

export default SearchPopup
